import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ComponentType } from 'react';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  TextField,
} from '@mui/material';

import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Grid';
import Badge from '@mui/material/Badge';

import { ExpandMoreRounded, TextsmsRounded } from '@mui/icons-material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { io } from 'socket.io-client';
import { debounce } from 'lodash';

import api from '../../../services/api';
import { ConfirmationTable } from '../../../modules/accommodation-provider/components/ConfirmationTable';
import SelectStaffCell from './SelectStaffCell';
import { DateField } from './fields/DateField';
import { TimeField } from './fields/TimeField';
import { useStateContext } from '../../../hooks/useStateContext';
import { comment } from '../../../schema/comment';

interface Props {
  confirmed?: boolean;
  item: any;
  onRefresh: () => void;
  onOpenChat: () => void;
  editableFields: any;
}

const SOCKET_URL = 'https://socket-zoe.stagemporium.com';

export const ServiceReservationConfirmationCard: ComponentType<Props> = ({
                                                                           item,
                                                                           onRefresh,
                                                                           confirmed,
                                                                           editableFields,
                                                                           onOpenChat,
                                                                         }) => {
  const { user } = useStateContext();

  const [buttonLoading, setButtonLoading] = useState<false | 'accept' | 'decline' | 'offer' | 'message' | 'room-offer'>(false);
  const [openOffer, setOpenOffer] = useState(false);
  const [offers, setOffers] = useState<any[]>([]);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [reservationIds, setReservationIds] = useState<number[]>([]);
  const [sending, setSending] = useState(false);

  const [roomsDraft, setRoomsDraft] = useState<any[]>([]);
  const [roomsDirty, setRoomsDirty] = useState(false);


  // Chat input (controlled)
  const [draft, setDraft] = useState<string>('');

  // If you want a “committed” (debounced) value for something else later,
  // you can keep it—sending uses `draft` directly for correctness.
  const [debouncedDraft, setDebouncedDraft] = useState<string>('');

  const debouncedUpdate = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedDraft(value);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const isRequest = !!item?.new;

  const isCancelled = useMemo(() => {
    const type = item?.formatted?.type ?? '';
    return String(type)
      .split('→')
      .some((t: string) => t?.includes?.('Cancelled'));
  }, [item?.formatted?.type]);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));

    if (field === 'rooms') {
      setRoomsDraft(value);
      setRoomsDirty(true);
    }
  }, []);

  const removeReservationId = useCallback((id: number) => {
    setReservationIds((prev) => prev.filter((rId) => rId !== id));
  }, []);

  const fetchNotifications = useCallback(() => {
    api.get('/notifications').then((result: any) => {
      const newIds = result.data
        .filter((n: any) => {
          return (
            n?.type === 'service-reservation-message' &&
            n?.service_reservation_id
          );
        })
        .map((n: any) => n.service_reservation_id as number);

      if (newIds.length > 0) {
        setReservationIds((prev) => {
          const merged = new Set(prev);
          newIds.forEach((id:any) => merged.add(id));
          return Array.from(merged);
        });
      }
    });
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Socket notifications (fix: no stale reservationIds/user closure)
  useEffect(() => {
    if (!user?.id) return;

    const socket = io(SOCKET_URL);

    socket.on('notification', (data: any) => {
      if (
        data?.notified_user_id === user?.id &&
        data?.data?.type === 'service-reservation-message'
      ) {
        const newId = data?.data?.service_reservation?.id;
        if (!newId) return;

        setReservationIds((prev) =>
          prev.includes(newId) ? prev : [...prev, newId]
        );
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.id]);

  const onSubmit = useCallback(
    async (
      action: 'accept' | 'decline' | 'offer' | 'message' | 'room-offer',
      reservationId: any,
      confirmationId: any,
      offerPayload?: any
    ) => {
      setButtonLoading(action);

      const patchIfNeeded = async () => {
        if (Object.keys(formValues).length === 0) return;

        try {
          await api.patch(
            `/accommodation-provider/accommodation-reservation-confirmations/${reservationId}/${confirmationId}`,
            formValues
          );
        } catch (error) {
          console.warn('PATCH hiba:', error);
        }
      };

      try {


        await api.post(
          `/accommodation-provider/accommodation-reservation-confirmations/${reservationId}/${confirmationId}`,
          {
            action,
            offers: offerPayload,
            message: draft,
            rooms: action === 'room-offer' ? roomsDraft : undefined,
          }
        );

        // Important: clear dirty fields after successful submit
        setFormValues({});
        setRoomsDirty(false);
        setRoomsDraft([]);

        onRefresh();
      } catch (error) {
        console.warn('POST hiba:', error);
      } finally {
        setButtonLoading(false);
      }
    },
    [formValues, onRefresh, draft]
  );

  const handleOffer = useCallback((modification: any) => {
    let date = '2025-02-10';
    if (modification?.raw_formatted?.date && modification?.formatted?.date) {
      date = modification.formatted.date
        .replace(/\s*\(.*?\)/, '')
        .replace(/\./g, '-')
        .slice(0, -1);
    }

    let time = '11:00';
    if (modification?.raw_formatted?.start_time) {
      time = modification.raw_formatted.start_time;
    }

    setOpenOffer(true);
    setOffers([{ date, time }]);
  }, []);

  const addAnotherOffer = useCallback(() => {
    let date = '2025-02-10';
    if (item?.formatted?.date) {
      date = item.formatted.date
        .replace(/\s*\(.*?\)/, '')
        .replace(/\./g, '-')
        .slice(0, -1);
    }
    if (item?.formatted?.non_formatted_date) {
      date = item.formatted.non_formatted_date
        .replace(/\s*\(.*?\)/, '')
        .replace(/\./g, '-')
        .slice(0, -1);
    }

    let time = '11:00';
    if (item?.formatted?.start_time) time = item.formatted.start_time;
    if (item?.formatted?.non_formatted_start_time) time = item.formatted.non_formatted_start_time;

    setOffers((prev) => [...prev, { date, time }]);
  }, [item]);

  const sendMessage = useCallback(() => {
    const text = draft.trim();
    if (!text) return;

    setSending(true);

    const payload: any = {
      user,
      comment: text,
      accommodation_reservation: item?.accommodation_reservation,
    };

    comment.client
      .create(payload)
      .then(() => {
        setDraft('');
        setDebouncedDraft('');
      })
      .catch(() => {})
      .finally(() => {
        setSending(false);
      });
  }, [draft, item?.accommodation_reservation, user]);

  const mobileHeaderSx = {
    '@media (max-width:600px)': { p: '8px' },
  };

  const mobileContentSx = {
    '@media (max-width:600px)': { padding: '0px 8px' },
  };

  return (
    <Card
      sx={{
        width: '100%',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <Chip
        sx={{
          position: 'absolute',
          top: '-10px',
          right: 0,
          height: '20px',
        }}
        size="small"
        label={
          <Typography variant="caption">
            {item?.confirmation_status?.name}
          </Typography>
        }
        color={
          item?.confirmation_status?.id
            ? item.confirmation_status.id === 1
              ? 'info'
              : item.confirmation_status.id === 2
                ? 'success'
                : item.confirmation_status.id === 3
                  ? 'warning'
                  : 'error'
            : 'default'
        }
      />

      <CardHeader
        title={
          <>
            Group Name:{' '}
            <b dangerouslySetInnerHTML={{ __html: item?.formatted?.group }} />
          </>
        }
        subheader={
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>{item?.created_at}</Typography>

            <Tooltip title="Chat megnyitása">
              <IconButton
                size="small"
                onClick={() => {
                  onOpenChat();
                  removeReservationId(item?.service_reservation_id);
                }}
              >
                <Badge
                  color="error"
                  variant="dot"
                  invisible={!reservationIds.includes(item?.service_reservation_id)}
                >
                  <TextsmsRounded color="primary" />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>
        }
        sx={mobileHeaderSx}
      />

      <CardContent sx={mobileContentSx}>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          <ConfirmationTable
            item={item}
            editableFields={editableFields}
            onChange={(field, value) => handleFieldChange(field, value)}
          />

          {item?.assigned_staffs && <SelectStaffCell rows={item?.assigned_staffs} />}
        </Typography>
      </CardContent>

      <CardActions
        sx={{
          alignItems: 'flex-start',
        }}
      >
        {roomsDirty ? (
          <Stack width="100%">
            <LoadingButton
              fullWidth
              variant="contained"
              color="primary"
              loading={buttonLoading === 'room-offer'}
              disabled={!!buttonLoading}
              onClick={() =>
                onSubmit('room-offer', item?.accommodation_reservation_id, item?.id, roomsDraft)
              }
            >
              Módosítás küldése
            </LoadingButton>
          </Stack>
        ) : (
          <>
            {openOffer && offers.length ? (
              <Stack gap={1} justifyContent="start" alignItems="flex-start" width="100%">
                {offers.map((offer: any, index: number) => (
                  <Stack
                    key={`${item?.id}-offer-${index}`}
                    direction="row"
                    spacing={1}
                    alignContent="start"
                    width="100%"
                  >
                    <DateField
                      value={offer?.date}
                      onChange={(value: any) => {
                        setOffers((prevOffers) =>
                          prevOffers.map((prevOffer, idx) =>
                            idx === index ? { ...prevOffer, date: value } : prevOffer
                          )
                        );
                      }}
                    />

                    <TimeField
                      value={offer?.time}
                      onChange={(value: any) => {
                        setOffers((prevOffers) =>
                          prevOffers.map((prevOffer, idx) =>
                            idx === index ? { ...prevOffer, time: value } : prevOffer
                          )
                        );
                      }}
                    />
                  </Stack>
                ))}

                <Stack direction="row" justifyContent="space-between" width="100%">
                  <Tooltip title="Add another offer">
                    <IconButton color="primary" onClick={addAnotherOffer}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>

                  <LoadingButton
                    loading={buttonLoading === 'offer'}
                    disabled={!!buttonLoading}
                    variant="contained"
                    color="primary"
                    onClick={() => onSubmit('offer', item?.accommodation_reservation_id, item?.id, offers)}
                  >
                    Elküldöm
                  </LoadingButton>
                </Stack>
              </Stack>
            ) : (
              <>
                {isRequest ? (
                  <Stack
                    spacing={1}
                    direction="row"
                    alignItems="flex-start"
                    sx={{
                      width: '100%'
                    }}
                  >
                    <TextField
                      disabled={sending}
                      id="outlined-multiline-flexible"
                      label="Ide írd az üzenetet..."
                      multiline
                      size="small"
                      fullWidth
                      maxRows={3}
                      value={draft}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '30px !important',
                          border: '1px solid #ededed',
                        },
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraft(value);
                        debouncedUpdate(value);
                      }}
                      onKeyDown={(e) => {
                        // Enter = send, Shift+Enter = newline
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />

                    <IconButton
                      disabled={sending || !draft.trim()}
                      sx={{
                        background: '#3e81fb',
                        '&:hover': { background: '#2f70e7 !important' },
                        '&.Mui-disabled': {
                          opacity: 0.6,
                          background: '#3e81fb',
                        },
                      }}
                      onClick={() => onSubmit('message', item?.accommodation_reservation_id, item?.id)}
                    >
                      <SendRoundedIcon sx={{ color: '#fff' }} />
                    </IconButton>
                  </Stack>
                ) : (
                  <>
                    {!confirmed && (
                      <Grid container spacing={1}>
                        {!isCancelled && (
                          <Grid item xs={12} md={6} lg={6}>
                            <LoadingButton
                              fullWidth
                              variant="outlined"
                              color="error"
                              loading={buttonLoading === 'decline'}
                              disabled={!!buttonLoading}
                              onClick={() =>
                                onSubmit('decline', item?.accommodation_reservation_id, item?.id)
                              }
                            >
                              Elutasítom
                            </LoadingButton>
                          </Grid>
                        )}

                        <Grid item xs={12} md={6} lg={6}>
                          <LoadingButton
                            fullWidth
                            variant="contained"
                            color="success"
                            loading={buttonLoading === 'accept'}
                            disabled={!!buttonLoading}
                            onClick={() =>
                              onSubmit('accept', item?.accommodation_reservation_id, item?.id)
                            }
                          >
                            Elfogadom
                          </LoadingButton>
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </CardActions>
    </Card>
  );
};
